/**
 * 팝업 하단 슬라이드 시트 (BOTTOM_SHEET).
 *
 * dismiss 정책: CenterModal과 동일 (버튼 한 줄).
 *
 * 영구지침: docs/develop/popup/popup-spec.md §9.4
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageCircle, X } from 'lucide-react'
import type { PopupResponse } from '@/features/popup/types'
import { recordClick, recordDismiss, recordImpression } from '@/features/popup/api'
import PopupMarkdown from './PopupMarkdown'
import {
  getDismissOptions,
  markDismissedFor,
} from '@/lib/popup/dismissStorage'

interface Props {
  popup: PopupResponse
}

export default function BottomSheet({ popup }: Props) {
  const navigate = useNavigate()
  const [visible, setVisible] = useState(true)

  const dismissOptions = useMemo(
    () => (popup.allowDismiss ? getDismissOptions(popup.dismissCooldownHours) : []),
    [popup.allowDismiss, popup.dismissCooldownHours]
  )

  useEffect(() => {
    void recordImpression(popup.id)
  }, [popup.id])

  if (!visible) return null

  const handleClick = () => {
    void recordClick(popup.id)
    if (popup.ctaUrl) navigate(popup.ctaUrl)
    setVisible(false)
  }

  const handleClose = () => {
    if (!popup.allowDismiss) return
    void recordDismiss(popup.id)
    // 그냥 닫기는 저장하지 않는다 → 새로고침·다음 방문 시 다시 노출. 기간 억제는 별도 버튼에서만.
    setVisible(false)
  }

  const handleDismissFor = (hours: number | null) => {
    void recordDismiss(popup.id)
    markDismissedFor(popup.id, hours)
    setVisible(false)
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-16 z-30 mx-auto max-w-md px-4 sm:bottom-4"
    >
      <div className="rounded-2xl bg-white p-4 shadow-lg ring-1 ring-slate-200">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
            <MessageCircle className="h-5 w-5 text-blue-600" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-slate-900">{popup.title}</h2>
            {popup.body && (
              <PopupMarkdown className="mt-0.5 text-xs text-slate-600">
                {popup.body}
              </PopupMarkdown>
            )}
            {popup.ctaLabel && (
              <button
                type="button"
                onClick={handleClick}
                className="mt-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                {popup.ctaLabel}
              </button>
            )}
          </div>
          {popup.allowDismiss && (
            <button
              type="button"
              onClick={handleClose}
              aria-label="닫기"
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* dismiss 버튼 한 줄 */}
        {popup.allowDismiss && dismissOptions.length > 0 && (
          <div className="mt-2.5 flex items-center justify-end gap-1 border-t border-slate-100 pt-2">
            {dismissOptions.map((option) => (
              <button
                key={option.hours ?? 'permanent'}
                type="button"
                onClick={() => handleDismissFor(option.hours)}
                className="px-2 py-1 text-[11px] text-slate-500 transition hover:text-slate-700"
              >
                {option.label}
              </button>
            ))}
            <span className="text-slate-200">|</span>
            <button
              type="button"
              onClick={handleClose}
              className="px-2 py-1 text-[11px] font-medium text-slate-600 transition hover:text-slate-900"
            >
              닫기
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
